import React, { useState, useMemo } from 'react';
import { 
  Building2, 
  Search, 
  Filter, 
  Edit3, 
  Check, 
  Copy, 
  ExternalLink, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  AlertCircle, 
  Save, 
  X, 
  RefreshCw, 
  Plus, 
  Download, 
  Layers, 
  FileText, 
  CheckCircle2, 
  Clock, 
  KeyRound, 
  Lock, 
  Globe, 
  Sparkles,
  ArrowUpDown,
  Landmark
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { ALL_NASPO_STATES, getNaspoStateByCode, NASPO_REGIONS, NaspoRegionFilter } from '../data/naspoStateData';
import { StateVendorCredential, StateRegistrationStatusFilter } from '../types/stateCredentials';
import { VendorProfileSwitcher } from './VendorProfileSwitcher';

interface StateVendorCredentialsManagerProps {
  embedded?: boolean;
  onOpenStateDossier?: (stateCode: string) => void;
}

export const StateVendorCredentialsManager: React.FC<StateVendorCredentialsManagerProps> = ({
  embedded = false,
  onOpenStateDossier
}) => {
  const { vendorProfile, activeProfileId, updateStateCredential, batchUpdateStateCredentials } = useAuth();

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<NaspoRegionFilter>('All');
  const [statusFilter, setStatusFilter] = useState<StateRegistrationStatusFilter>('all');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [sortField, setSortField] = useState<'state' | 'status' | 'vendorId' | 'updatedAt'>('state');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Editing Drawer/Modal State
  const [activeEditingCode, setActiveEditingCode] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState<StateVendorCredential | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Bulk Operations State
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState<boolean>(false);
  const [bulkUsername, setBulkUsername] = useState<string>('');
  const [bulkPassword, setBulkPassword] = useState<string>('');
  const [bulkStatus, setBulkStatus] = useState<'active' | 'pending' | 'not_registered' | 'unchanged'>('unchanged');
  const [isBulkSaving, setIsBulkSaving] = useState<boolean>(false);

  // Current entity state registrations from Firestore
  const stateRegistrations = useMemo(() => {
    return (vendorProfile?.stateRegistrations || {}) as Record<string, StateVendorCredential>;
  }, [vendorProfile]);

  // Combined state profiles with credentials data
  const combinedStateList = useMemo(() => {
    return ALL_NASPO_STATES.map(naspo => {
      const cred: Partial<StateVendorCredential> = stateRegistrations[naspo.stateCode.toUpperCase()] || {};
      const isCharterState = Boolean(
        vendorProfile?.businessRegisteredState &&
        (vendorProfile.businessRegisteredState.toLowerCase() === naspo.state.toLowerCase() ||
         vendorProfile.businessRegisteredState.toUpperCase() === naspo.stateCode.toUpperCase())
      );

      const isRegistered = cred.isRegistered ?? false;
      const status = cred.registrationStatus || (isRegistered ? 'active' : 'not_registered');

      return {
        ...naspo,
        isCharterState,
        credential: {
          stateCode: naspo.stateCode.toUpperCase(),
          stateName: naspo.state,
          isRegistered,
          registrationStatus: status,
          vendorId: cred.vendorId || '',
          username: cred.username || '',
          password: cred.password || '',
          stateTaxId: cred.stateTaxId || '',
          stateLicenseNumber: cred.stateLicenseNumber || '',
          dbeCertificationNumber: cred.dbeCertificationNumber || '',
          dbeExpirationDate: cred.dbeExpirationDate || '',
          contactName: cred.contactName || '',
          contactEmail: cred.contactEmail || '',
          contactPhone: cred.contactPhone || '',
          customLoginUrl: cred.customLoginUrl || naspo.vendorUrl || naspo.procurementUrl || '',
          portalUrl: cred.portalUrl || naspo.vendorUrl || naspo.procurementUrl || '',
          notes: cred.notes || '',
          updatedAt: cred.updatedAt || ''
        } as StateVendorCredential
      };
    });
  }, [stateRegistrations, vendorProfile]);

  // Filtered & Sorted States
  const filteredStates = useMemo(() => {
    return combinedStateList.filter(item => {
      const q = searchTerm.toLowerCase().trim();
      const matchesSearch = !q || 
        item.state.toLowerCase().includes(q) ||
        item.stateCode.toLowerCase().includes(q) ||
        item.capital.toLowerCase().includes(q) ||
        item.agencyName?.toLowerCase().includes(q) ||
        (item.credential.vendorId && item.credential.vendorId.toLowerCase().includes(q)) ||
        (item.credential.username && item.credential.username.toLowerCase().includes(q));

      const matchesRegion = selectedRegion === 'All' || item.region === selectedRegion;

      let matchesStatus = true;
      if (statusFilter === 'registered') {
        matchesStatus = item.credential.isRegistered || item.credential.registrationStatus === 'active';
      } else if (statusFilter === 'pending') {
        matchesStatus = item.credential.registrationStatus === 'pending';
      } else if (statusFilter === 'not_registered') {
        matchesStatus = !item.credential.isRegistered && item.credential.registrationStatus !== 'pending';
      } else if (statusFilter === 'needs_credentials') {
        matchesStatus = !item.credential.vendorId && !item.credential.username;
      }

      return matchesSearch && matchesRegion && matchesStatus;
    }).sort((a, b) => {
      let comparison = 0;
      if (sortField === 'state') {
        comparison = a.state.localeCompare(b.state);
      } else if (sortField === 'status') {
        const scoreA = a.credential.isRegistered ? 2 : (a.credential.registrationStatus === 'pending' ? 1 : 0);
        const scoreB = b.credential.isRegistered ? 2 : (b.credential.registrationStatus === 'pending' ? 1 : 0);
        comparison = scoreB - scoreA;
      } else if (sortField === 'vendorId') {
        comparison = (a.credential.vendorId || '').localeCompare(b.credential.vendorId || '');
      } else if (sortField === 'updatedAt') {
        comparison = (a.credential.updatedAt || '').localeCompare(b.credential.updatedAt || '');
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [combinedStateList, searchTerm, selectedRegion, statusFilter, sortField, sortOrder]);

  // Statistics
  const stats = useMemo(() => {
    const total = combinedStateList.length;
    const registered = combinedStateList.filter(s => s.credential.isRegistered || s.credential.registrationStatus === 'active').length;
    const pending = combinedStateList.filter(s => s.credential.registrationStatus === 'pending').length;
    const withCredentials = combinedStateList.filter(s => s.credential.vendorId || s.credential.username).length;
    const charter = combinedStateList.find(s => s.isCharterState);

    return { total, registered, pending, withCredentials, charter };
  }, [combinedStateList]);

  // Handle open editor for a specific state
  const handleOpenEdit = (stateCode: string) => {
    const item = combinedStateList.find(s => s.stateCode.toUpperCase() === stateCode.toUpperCase());
    if (!item) return;

    setEditingForm({
      ...item.credential,
      stateCode: item.stateCode.toUpperCase(),
      stateName: item.state,
      customLoginUrl: item.credential.customLoginUrl || item.vendorUrl || item.procurementUrl || ''
    });
    setActiveEditingCode(item.stateCode.toUpperCase());
    setShowPassword(false);
  };

  // Save changes to current state credential
  const handleSaveStateCredential = async () => {
    if (!editingForm || !activeEditingCode) return;
    setIsSaving(true);

    try {
      await updateStateCredential(activeEditingCode, editingForm);
      setToastMessage(`Vendor credentials saved for ${editingForm.stateName}!`);
      setActiveEditingCode(null);
      setEditingForm(null);
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err: any) {
      console.error(err);
      setToastMessage(`Error saving credentials: ${err.message}`);
      setTimeout(() => setToastMessage(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  // Quick Copy Helper
  const handleCopy = (text: string, key: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Bulk Selection Toggles
  const handleSelectAll = () => {
    if (selectedStates.length === filteredStates.length) {
      setSelectedStates([]);
    } else {
      setSelectedStates(filteredStates.map(s => s.stateCode.toUpperCase()));
    }
  };

  const handleToggleStateSelect = (code: string) => {
    const upper = code.toUpperCase();
    if (selectedStates.includes(upper)) {
      setSelectedStates(selectedStates.filter(c => c !== upper));
    } else {
      setSelectedStates([...selectedStates, upper]);
    }
  };

  // Apply Bulk Credentials Update
  const handleExecuteBulkUpdate = async () => {
    if (selectedStates.length === 0) return;
    setIsBulkSaving(true);

    try {
      const updates: Record<string, Partial<StateVendorCredential>> = {};
      selectedStates.forEach(code => {
        const stateObj = getNaspoStateByCode(code);
        const itemUpdate: Partial<StateVendorCredential> = {
          stateCode: code,
          stateName: stateObj?.state || code
        };

        if (bulkUsername.trim()) {
          itemUpdate.username = bulkUsername.trim();
        }
        if (bulkPassword.trim()) {
          itemUpdate.password = bulkPassword.trim();
        }
        if (bulkStatus === 'active') {
          itemUpdate.isRegistered = true;
          itemUpdate.registrationStatus = 'active';
        } else if (bulkStatus === 'pending') {
          itemUpdate.isRegistered = false;
          itemUpdate.registrationStatus = 'pending';
        } else if (bulkStatus === 'not_registered') {
          itemUpdate.isRegistered = false;
          itemUpdate.registrationStatus = 'not_registered';
        }

        updates[code] = itemUpdate;
      });

      await batchUpdateStateCredentials(updates);
      setToastMessage(`Successfully updated credentials for ${selectedStates.length} states!`);
      setShowBulkModal(false);
      setSelectedStates([]);
      setBulkUsername('');
      setBulkPassword('');
      setBulkStatus('unchanged');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err: any) {
      console.error(err);
      setToastMessage(`Bulk update failed: ${err.message}`);
      setTimeout(() => setToastMessage(null), 4000);
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Export Credentials CSV
  const handleExportCSV = () => {
    const headers = [
      'State Code',
      'State Name',
      'Registration Status',
      'Is Registered',
      'State Vendor ID',
      'Portal Username',
      'State Tax ID',
      'State License #',
      'DBE/HUB Cert #',
      'Contact Name',
      'Contact Email',
      'Contact Phone',
      'Portal URL',
      'Last Updated'
    ];

    const rows = combinedStateList.map(s => [
      s.stateCode,
      `"${s.state}"`,
      s.credential.registrationStatus || 'not_registered',
      s.credential.isRegistered ? 'YES' : 'NO',
      `"${s.credential.vendorId || ''}"`,
      `"${s.credential.username || ''}"`,
      `"${s.credential.stateTaxId || ''}"`,
      `"${s.credential.stateLicenseNumber || ''}"`,
      `"${s.credential.dbeCertificationNumber || ''}"`,
      `"${s.credential.contactName || ''}"`,
      `"${s.credential.contactEmail || ''}"`,
      `"${s.credential.contactPhone || ''}"`,
      `"${s.credential.customLoginUrl || s.vendorUrl || ''}"`,
      `"${s.credential.updatedAt || ''}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `state_vendor_credentials_${vendorProfile?.companyName?.replace(/\s+/g, '_') || 'entity'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full space-y-4 text-slate-800 animate-fade-in" id="state-vendor-credentials-manager">
      {/* Toast Alert */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl border border-slate-700 flex items-center gap-3 animate-in slide-in-from-bottom-3 duration-200">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold">{toastMessage}</span>
          <button 
            onClick={() => setToastMessage(null)}
            className="text-slate-400 hover:text-white p-0.5 cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Header Banner & Multi-Entity Switcher */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200/80 flex items-center justify-center font-bold shrink-0 shadow-xs">
              <KeyRound className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-slate-900">
                  State Vendor Credentials & Supplier Portals
                </h2>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-emerald-100/80 text-emerald-900 border border-emerald-200">
                  50 States + DC
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200/60">
                  {vendorProfile?.companyName || 'Active Entity'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Manage, update, and synchronize state vendor IDs, portal logins, taxpayer credentials, and certification records for <strong>{vendorProfile?.companyName || 'your active business profile'}</strong>.
              </p>
            </div>
          </div>

          {/* Quick Entity Switcher & Action Controls */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
            <VendorProfileSwitcher variant="compact" />
            <button
              onClick={handleExportCSV}
              id="btn-export-credentials-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-all cursor-pointer"
              title="Export 50-State Credentials CSV"
            >
              <Download className="w-3.5 h-3.5 text-slate-600" />
              <span>Export CSV</span>
            </button>
            {selectedStates.length > 0 && (
              <button
                onClick={() => setShowBulkModal(true)}
                id="btn-bulk-edit-credentials"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-xs transition-all cursor-pointer"
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Bulk Update ({selectedStates.length})</span>
              </button>
            )}
          </div>
        </div>

        {/* 4 High-Density KPI Metric Tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 text-xs">
          {/* Total Registered */}
          <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-xl p-3 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Actively Registered</span>
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-xl font-black text-emerald-950">{stats.registered}</span>
              <span className="text-[11px] font-medium text-emerald-700">of {stats.total} jurisdictions</span>
            </div>
          </div>

          {/* Pending Submissions */}
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider">Pending Submissions</span>
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-xl font-black text-amber-950">{stats.pending}</span>
              <span className="text-[11px] font-medium text-amber-700">in review</span>
            </div>
          </div>

          {/* Stored Credentials Vault */}
          <div className="bg-indigo-50/70 border border-indigo-200/80 rounded-xl p-3 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-indigo-800 uppercase tracking-wider">Credentials Vault</span>
              <KeyRound className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-xl font-black text-indigo-950">{stats.withCredentials}</span>
              <span className="text-[11px] font-medium text-indigo-700">portals configured</span>
            </div>
          </div>

          {/* Domestic Charter State */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Charter Jurisdiction</span>
              <Landmark className="w-4 h-4 text-slate-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-1.5 truncate">
              <span className="text-sm font-black text-slate-900 truncate">
                {vendorProfile?.businessRegisteredState || 'South Dakota'}
              </span>
              <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800 shrink-0">
                Domestic
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by state name, code, vendor ID, username, or capital..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Region Tabs */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl overflow-x-auto text-xs">
            {NASPO_REGIONS.map(reg => (
              <button
                key={reg}
                onClick={() => setSelectedRegion(reg)}
                className={`px-2.5 py-1 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer ${
                  selectedRegion === reg
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {reg}
              </button>
            ))}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs shrink-0">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                viewMode === 'table' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                viewMode === 'cards' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Cards
            </button>
          </div>
        </div>

        {/* Status Filters & Selection Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100 text-xs">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-slate-400 uppercase mr-1">Filter Status:</span>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-0.8 rounded-md font-semibold transition-all cursor-pointer ${
                statusFilter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All ({combinedStateList.length})
            </button>
            <button
              onClick={() => setStatusFilter('registered')}
              className={`px-2.5 py-0.8 rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                statusFilter === 'registered' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/60'
              }`}
            >
              <CheckCircle2 className="w-3 h-3" /> Registered ({stats.registered})
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-2.5 py-0.8 rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1 ${
                statusFilter === 'pending' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/60'
              }`}
            >
              <Clock className="w-3 h-3" /> Pending ({stats.pending})
            </button>
            <button
              onClick={() => setStatusFilter('not_registered')}
              className={`px-2.5 py-0.8 rounded-md font-semibold transition-all cursor-pointer ${
                statusFilter === 'not_registered' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Not Registered ({combinedStateList.length - stats.registered - stats.pending})
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-bold cursor-pointer"
            >
              {selectedStates.length === filteredStates.length ? 'Deselect All' : `Select All (${filteredStates.length})`}
            </button>
          </div>
        </div>
      </div>

      {/* TABLE VIEW */}
      {viewMode === 'table' ? (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider select-none">
                  <th className="py-3 px-3 w-8">
                    <input 
                      type="checkbox"
                      checked={selectedStates.length > 0 && selectedStates.length === filteredStates.length}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 rounded text-emerald-600 cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-3 cursor-pointer hover:text-slate-900" onClick={() => { setSortField('state'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    <div className="flex items-center gap-1">
                      <span>State & Jurisdiction</span>
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-3 cursor-pointer hover:text-slate-900" onClick={() => { setSortField('status'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    <div className="flex items-center gap-1">
                      <span>Registration Status</span>
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-3 cursor-pointer hover:text-slate-900" onClick={() => { setSortField('vendorId'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}>
                    <div className="flex items-center gap-1">
                      <span>State Vendor / Supplier ID</span>
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-3">Portal Login / User ID</th>
                  <th className="py-3 px-3">Tax / Certification IDs</th>
                  <th className="py-3 px-3">State Portal</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-400">
                      No states match the specified filter or search criteria.
                    </td>
                  </tr>
                ) : (
                  filteredStates.map((item) => {
                    const cred = item.credential;
                    const isSelected = selectedStates.includes(item.stateCode.toUpperCase());

                    return (
                      <tr 
                        key={item.stateCode}
                        className={`hover:bg-slate-50/80 transition-colors ${isSelected ? 'bg-indigo-50/30' : ''}`}
                      >
                        <td className="py-3 px-3">
                          <input 
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleStateSelect(item.stateCode)}
                            className="w-3.5 h-3.5 rounded text-emerald-600 cursor-pointer"
                          />
                        </td>

                        {/* State & Jurisdiction */}
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2.5">
                            <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-700 font-mono font-bold flex items-center justify-center text-xs shrink-0 border border-slate-200">
                              {item.stateCode}
                            </span>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-slate-900">{item.state}</span>
                                {item.isCharterState && (
                                  <span className="text-[9px] font-black uppercase px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800">
                                    Charter
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-400 truncate max-w-48">
                                {item.agencyName || `NASPO ${item.region}`}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3 px-3">
                          {cred.isRegistered || cred.registrationStatus === 'active' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200/80">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Active Registered
                            </span>
                          ) : cred.registrationStatus === 'pending' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200/80">
                              <Clock className="w-3 h-3 text-amber-600" /> Pending Review
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                              Not Registered
                            </span>
                          )}
                        </td>

                        {/* Vendor ID */}
                        <td className="py-3 px-3">
                          {cred.vendorId ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-0.8 rounded border border-slate-200 text-xs">
                                {cred.vendorId}
                              </span>
                              <button
                                onClick={() => handleCopy(cred.vendorId!, `vid_${item.stateCode}`)}
                                className="text-slate-400 hover:text-slate-700 p-0.5 cursor-pointer"
                                title="Copy Vendor ID"
                              >
                                {copiedKey === `vid_${item.stateCode}` ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic text-[11px]">Unassigned</span>
                          )}
                        </td>

                        {/* Username */}
                        <td className="py-3 px-3">
                          {cred.username ? (
                            <div className="flex items-center gap-1.5 max-w-44">
                              <span className="font-mono text-slate-700 text-xs truncate" title={cred.username}>
                                {cred.username}
                              </span>
                              <button
                                onClick={() => handleCopy(cred.username!, `usr_${item.stateCode}`)}
                                className="text-slate-400 hover:text-slate-700 p-0.5 cursor-pointer shrink-0"
                                title="Copy Username"
                              >
                                {copiedKey === `usr_${item.stateCode}` ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic text-[11px]">No Login</span>
                          )}
                        </td>

                        {/* Tax / Certifications */}
                        <td className="py-3 px-3">
                          <div className="space-y-0.5 text-[10px]">
                            {cred.stateTaxId && (
                              <div className="text-slate-600 font-mono">Tax ID: {cred.stateTaxId}</div>
                            )}
                            {cred.dbeCertificationNumber && (
                              <div className="text-indigo-600 font-semibold">Cert: {cred.dbeCertificationNumber}</div>
                            )}
                            {!cred.stateTaxId && !cred.dbeCertificationNumber && (
                              <span className="text-slate-400 italic">None</span>
                            )}
                          </div>
                        </td>

                        {/* Portal Links */}
                        <td className="py-3 px-3">
                          {(cred.customLoginUrl || item.vendorUrl || item.procurementUrl) && (
                            <a
                              href={cred.customLoginUrl || item.vendorUrl || item.procurementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                            >
                              <span>Portal</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenEdit(item.stateCode)}
                              id={`btn-edit-state-cred-${item.stateCode.toLowerCase()}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 rounded-lg border border-slate-200 transition-all cursor-pointer"
                            >
                              <Edit3 className="w-3 h-3" />
                              <span>Update</span>
                            </button>
                            {onOpenStateDossier && (
                              <button
                                onClick={() => onOpenStateDossier(item.stateCode)}
                                className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                                title="Open State Dossier & ROSP Rules"
                              >
                                <FileText className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* CARD GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredStates.map((item) => {
            const cred = item.credential;
            const isSelected = selectedStates.includes(item.stateCode.toUpperCase());

            return (
              <div 
                key={item.stateCode}
                className={`bg-white border rounded-2xl p-4 shadow-xs transition-all flex flex-col justify-between ${
                  isSelected ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div>
                  {/* Top Card Bar */}
                  <div className="flex items-start justify-between gap-2 pb-3 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <input 
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleStateSelect(item.stateCode)}
                        className="w-3.5 h-3.5 rounded text-emerald-600 cursor-pointer mr-0.5"
                      />
                      <span className="w-8 h-8 rounded-xl bg-slate-100 text-slate-800 font-mono font-bold flex items-center justify-center text-xs border border-slate-200 shrink-0">
                        {item.stateCode}
                      </span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-bold text-slate-900 text-sm">{item.state}</h3>
                          {item.isCharterState && (
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.2 rounded bg-emerald-100 text-emerald-800">
                              Charter
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 truncate max-w-40">{item.capital}, {item.region}</p>
                      </div>
                    </div>

                    {/* Status Pill */}
                    {cred.isRegistered || cred.registrationStatus === 'active' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Active
                      </span>
                    ) : cred.registrationStatus === 'pending' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                        <Clock className="w-3 h-3 text-amber-600" /> Pending
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-600">
                        Not Registered
                      </span>
                    )}
                  </div>

                  {/* Credentials Attributes */}
                  <div className="py-3 space-y-2 text-xs">
                    {/* Vendor ID */}
                    <div className="flex items-center justify-between gap-1 bg-slate-50 p-2 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Vendor ID:</span>
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-bold text-slate-800 text-xs">
                          {cred.vendorId || <span className="text-slate-400 italic font-normal">None</span>}
                        </span>
                        {cred.vendorId && (
                          <button
                            onClick={() => handleCopy(cred.vendorId!, `card_vid_${item.stateCode}`)}
                            className="text-slate-400 hover:text-slate-700 p-0.5 cursor-pointer"
                          >
                            {copiedKey === `card_vid_${item.stateCode}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Username */}
                    <div className="flex items-center justify-between gap-1 bg-slate-50 p-2 rounded-xl border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Portal Login:</span>
                      <div className="flex items-center gap-1 max-w-44">
                        <span className="font-mono text-slate-700 text-xs truncate">
                          {cred.username || <span className="text-slate-400 italic">None</span>}
                        </span>
                        {cred.username && (
                          <button
                            onClick={() => handleCopy(cred.username!, `card_usr_${item.stateCode}`)}
                            className="text-slate-400 hover:text-slate-700 p-0.5 cursor-pointer shrink-0"
                          >
                            {copiedKey === `card_usr_${item.stateCode}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Password Presence Indicator */}
                    <div className="flex items-center justify-between gap-1 text-[11px] px-1 text-slate-500">
                      <span>Password:</span>
                      <span className="font-mono text-slate-700">
                        {cred.password ? '••••••••••••' : <span className="text-slate-400 italic">Not set</span>}
                      </span>
                    </div>

                    {/* Notes / Tax ID snippet if present */}
                    {(cred.stateTaxId || cred.dbeCertificationNumber || cred.notes) && (
                      <div className="bg-amber-50/60 border border-amber-200/60 rounded-lg p-2 text-[10px] space-y-0.5">
                        {cred.stateTaxId && <div>Tax ID: <span className="font-mono font-bold text-slate-800">{cred.stateTaxId}</span></div>}
                        {cred.dbeCertificationNumber && <div className="text-indigo-800 font-semibold">DBE/Cert: {cred.dbeCertificationNumber}</div>}
                        {cred.notes && <div className="text-slate-600 line-clamp-1 italic">"{cred.notes}"</div>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Card Actions */}
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                  {(cred.customLoginUrl || item.vendorUrl || item.procurementUrl) ? (
                    <a
                      href={cred.customLoginUrl || item.vendorUrl || item.procurementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900"
                    >
                      <span>Portal</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : <div />}

                  <button
                    onClick={() => handleOpenEdit(item.stateCode)}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                  >
                    <Edit3 className="w-3 h-3" />
                    <span>Update Credentials</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* STATE CREDENTIALS EDITING MODAL */}
      {editingForm && activeEditingCode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white max-w-2xl w-full rounded-2xl p-6 shadow-2xl border border-slate-200 space-y-5 my-8 animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-3 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-900 font-mono font-black flex items-center justify-center text-sm border border-emerald-200">
                  {editingForm.stateCode}
                </span>
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Update Vendor Credentials — {editingForm.stateName}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Active Business Entity: <strong className="text-indigo-600">{vendorProfile?.companyName || 'Active Entity'}</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setActiveEditingCode(null); setEditingForm(null); }}
                className="text-slate-400 hover:text-slate-700 p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Fields */}
            <div className="space-y-4 text-xs">
              {/* Registration Status Radio & Checkbox */}
              <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  State Procurement Standing & Registration
                </label>
                <div className="flex flex-wrap gap-4 items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regStatus"
                      checked={editingForm.isRegistered && editingForm.registrationStatus === 'active'}
                      onChange={() => setEditingForm({ ...editingForm, isRegistered: true, registrationStatus: 'active' })}
                      className="text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span className="font-semibold text-emerald-800">Actively Registered</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regStatus"
                      checked={editingForm.registrationStatus === 'pending'}
                      onChange={() => setEditingForm({ ...editingForm, isRegistered: false, registrationStatus: 'pending' })}
                      className="text-amber-600 focus:ring-amber-500 cursor-pointer"
                    />
                    <span className="font-semibold text-amber-800">Application Pending</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="regStatus"
                      checked={!editingForm.isRegistered && editingForm.registrationStatus !== 'pending'}
                      onChange={() => setEditingForm({ ...editingForm, isRegistered: false, registrationStatus: 'not_registered' })}
                      className="text-slate-600 focus:ring-slate-500 cursor-pointer"
                    />
                    <span className="text-slate-600">Not Registered</span>
                  </label>
                </div>
              </div>

              {/* Core Credentials: Vendor ID & Username */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-700 uppercase">
                    State Vendor / Supplier ID / Bidder #
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. TX-984201 or 00004921"
                    value={editingForm.vendorId || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, vendorId: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-700 uppercase">
                    Portal Login / User ID / Email
                  </label>
                  <input
                    type="text"
                    placeholder="vendor_portal@company.com"
                    value={editingForm.username || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, username: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-700 uppercase">
                  Portal Password / Secret Access PIN
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={editingForm.password || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, password: e.target.value })}
                    className="w-full px-3 py-2 pr-10 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Additional State Identifiers: Tax ID, SOS License, DBE */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase">
                    State Tax / Withholding ID
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 192847192"
                    value={editingForm.stateTaxId || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, stateTaxId: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase">
                    SOS / Contractor License #
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. SOS-COA-8491"
                    value={editingForm.stateLicenseNumber || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, stateLicenseNumber: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-600 uppercase">
                    State DBE / HUB / MBE Cert #
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. HUB-2026-99"
                    value={editingForm.dbeCertificationNumber || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, dbeCertificationNumber: e.target.value })}
                    className="w-full px-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                  />
                </div>
              </div>

              {/* Portal URL & Point of Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-700 uppercase">
                    Portal Login / Solicitations URL
                  </label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="url"
                      placeholder="https://purchasing.state.gov/login"
                      value={editingForm.customLoginUrl || ''}
                      onChange={(e) => setEditingForm({ ...editingForm, customLoginUrl: e.target.value })}
                      className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    {editingForm.customLoginUrl && (
                      <a
                        href={editingForm.customLoginUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg shrink-0"
                        title="Test Open Portal"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-700 uppercase">
                    Assigned State Bid Contact / Email
                  </label>
                  <input
                    type="text"
                    placeholder="Contact Name or Email"
                    value={editingForm.contactName || ''}
                    onChange={(e) => setEditingForm({ ...editingForm, contactName: e.target.value })}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Internal Notes */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-700 uppercase">
                  Internal Notes & Renewal Milestones
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Annual renewal due March 31. Requires mandatory e-invoice portal registration."
                  value={editingForm.notes || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, notes: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setActiveEditingCode(null); setEditingForm(null); }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveStateCredential}
                disabled={isSaving}
                className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 transition-all cursor-pointer shadow-xs"
              >
                {isSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span>Save Credentials</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BULK UPDATE MODAL */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white max-w-lg w-full rounded-2xl p-6 shadow-2xl border border-slate-200 space-y-4 animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Bulk Update Credentials</h3>
                  <p className="text-xs text-slate-500">Applying changes to {selectedStates.length} selected state portals</p>
                </div>
              </div>
              <button onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-700 uppercase">Set Common Username / Email</label>
                <input
                  type="text"
                  placeholder="Leave blank to keep existing"
                  value={bulkUsername}
                  onChange={(e) => setBulkUsername(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-700 uppercase">Set Common Password</label>
                <input
                  type="text"
                  placeholder="Leave blank to keep existing"
                  value={bulkPassword}
                  onChange={(e) => setBulkPassword(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-700 uppercase">Update Status for Selected</label>
                <select
                  value={bulkStatus}
                  onChange={(e: any) => setBulkStatus(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                >
                  <option value="unchanged">Keep Current Status</option>
                  <option value="active">Mark as Actively Registered</option>
                  <option value="pending">Mark as Application Pending</option>
                  <option value="not_registered">Mark as Not Registered</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteBulkUpdate}
                disabled={isBulkSaving}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                {isBulkSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span>Apply to {selectedStates.length} States</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
